// src/hooks/auth/useLogin.ts
import { useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import {useToast} from "../components/common/toast/useToast.ts";

export interface LoginCredentials {
    identifier: string;
    password: string;
}

export interface AuthResponse {
    success: boolean;
    error?: string;
}

export const useLogin = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { signIn, setActive } = useSignIn();
    const navigate = useNavigate();
    const showToast = useToast();

    const login = async ({ identifier, password }: LoginCredentials): Promise<AuthResponse> => {
        setIsLoading(true);
        try {
            const result = await signIn?.create({
                identifier,
                password,
                strategy: "password",
            });

            if (result?.status === "complete" && result.createdSessionId) {
                if (setActive) {
                    await setActive({ session: result.createdSessionId });
                }

                showToast?.({
                    type: 'success',
                    title: 'Welcome to ThreatInformant!',
                    message: 'Successfully logged in'
                });

                navigate('/app/dashboard');
                return { success: true };
            }

            return { success: false, error: 'Login failed' };
        } catch (err: unknown) {
            const error = err instanceof Error ? err.message : 'Login failed';
            showToast?.({
                type: 'error',
                title: 'Login failed',
                message: error
            });
            return { success: false, error };
        } finally {
            setIsLoading(false);
        }
    };

    return { isLoading, login };
};